'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, UserCredential, onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useRouter } from 'next/navigation';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useSession } from 'next-auth/react';
import { Session } from 'next-auth'; // Import Session type
import { useAuthNavigation } from './hooks/useAuthNavigation';
import { useRoleDetermination } from './hooks/useRoleDetermination';

// Define an interface for the expected user structure within the NextAuth session
interface SessionUser {
  id?: string; // Assuming 'id' holds the student ID for students
  role?: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
}

// Extend the NextAuth Session type if needed, or use SessionUser directly
interface ExtendedSession extends Omit<Session, 'user'> {
  user?: SessionUser;
}

export interface AuthContextType {
  user: User | null;
  userData: any | null;
  childrenData: any[] | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  studentSession: any | null;
  userRole: string | null;
  localEffectiveRole: string | null; // The role being used in the current context
  isParent: boolean;
  isOperator: boolean;
  currentViewRole: 'parent' | 'operator' | null;
  setCurrentViewRole: (role: 'parent' | 'operator') => void;
  manualSyncWithLocalStorage: () => void;
  refreshUserData: () => Promise<void>;
  refreshChildrenData: () => Promise<void>;
  handleLoginSuccess: (customToken: string, studentIdHint?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  studentSession: null,
  userRole: null,
  localEffectiveRole: null,
  isParent: false,
  isOperator: false,
  currentViewRole: null,
  setCurrentViewRole: () => {},
  manualSyncWithLocalStorage: () => {},
  user: null,
  userData: null,
  childrenData: null,
  loading: true,
  error: null,
  refreshUserData: async () => {},
  refreshChildrenData: async () => {},
  handleLoginSuccess: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<any | null>(null);
  const [childrenData, setChildrenData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [studentSession, setStudentSession] = useState<any | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isProfessional, setIsProfessional] = useState(() => {
    // Initialize from localStorage for instant availability before Firebase loads
    if (typeof window !== 'undefined') {
      return localStorage.getItem('solynta_isProfessional') === 'true';
    }
    return false;
  });
  // Grace windows to prevent premature unauthenticated redirects right after auto-login
  const [studentDashGraceUntil, setStudentDashGraceUntil] = useState<number | null>(null);
  const [postPaymentGraceUntil, setPostPaymentGraceUntil] = useState<number | null>(null);
  
  // Initialize currentViewRole from localStorage
  const [currentViewRole, setCurrentViewRole] = useState<'parent' | 'operator' | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('current_view_role') as 'parent' | 'operator' | null;
    }
    return null;
  });

  // Enhanced setCurrentViewRole that persists the selection
  const updateCurrentViewRole = useCallback((role: 'parent' | 'operator') => {
    console.log(`AuthProvider: Updating current view role from ${currentViewRole} to: ${role}`);
    setCurrentViewRole(role);
    localStorage.setItem('current_view_role', role);
    console.log(`AuthProvider: Updated current view role to: ${role}`);
  }, [currentViewRole]);
  const { data: session, status } = useSession(); // session will be of type Session | null

  // Synchronize AuthProvider auth state with NextAuth session
  useEffect(() => {
    if (status === 'loading') return; // keep existing loading state until session known

    if (status === 'authenticated' && session?.user) {
      // Mark as authenticated
      if (!isAuthenticated) {
        console.log('AuthProvider: NextAuth session detected – marking as authenticated');
        setIsAuthenticated(true);
      }
      
      // Check for operator session in localStorage (set during operator login)
      const operatorSession = localStorage.getItem('operator_session');
      const storedViewRole = localStorage.getItem('current_view_role');
      
      // Store role from session if not set yet
      const sessRole = (session.user as SessionUser)?.role;

      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const shouldIgnoreNextAuthStudentRole =
        sessRole === 'student' &&
        (storedViewRole === 'operator' || !!operatorSession || currentPath.startsWith('/operator'));
      
      // If user logged in as operator but NextAuth session shows 'parent',
      // check if they have operator access via localStorage
      if (operatorSession && storedViewRole === 'operator') {
        try {
          const opSession = JSON.parse(operatorSession);
          if (opSession.role === 'operator' || opSession.role === 'both') {
            // Operator intent in localStorage should win over a stale NextAuth 'parent' role.
            if (userRole !== 'both') {
              console.log('AuthProvider: Detected operator session, upgrading role from parent to both');
              setUserRole('both');
              setCurrentViewRole('operator');
            }
          }
        } catch (e) {
          console.warn('AuthProvider: Failed to parse operator_session:', e);
          // If operator_session is malformed, fall back to NextAuth role sync.
          if (!shouldIgnoreNextAuthStudentRole && sessRole && userRole !== sessRole) {
            console.log('AuthProvider: Syncing userRole from NextAuth session:', sessRole);
            setUserRole(sessRole);
          }
        }
      } else if (!shouldIgnoreNextAuthStudentRole && sessRole && userRole !== sessRole) {
        console.log('AuthProvider: Syncing userRole from NextAuth session:', sessRole);
        setUserRole(sessRole);
      }
      
      if (loading) {
        setLoading(false);
      }
    } else if (status === 'unauthenticated') {
      // Only mark as unauthenticated if Firebase Auth also has no user
      // This prevents clearing state when NextAuth session expires but Firebase session is still valid
      if (isAuthenticated && !user) {
        console.log('AuthProvider: Session lost – marking as unauthenticated');
        setIsAuthenticated(false);
      }
      if (loading) {
        setLoading(false);
      }
    }
  }, [status, session, isAuthenticated, userRole, loading]);
  const router = useRouter();

  // Refs to track the latest NextAuth session/status inside callbacks
  const prevAuthStatusRef = useRef<string | null>(null);
  const nextAuthStatusRef = useRef(status);
  const nextAuthSessionRef = useRef(session);

  useEffect(() => {
    nextAuthStatusRef.current = status;
  }, [status]);

  useEffect(() => {
    nextAuthSessionRef.current = session;
  }, [session]);

  // Computed properties for role checking
  const isParent = userRole === 'parent' || userRole === 'both';
  const isOperator = userRole === 'operator' || userRole === 'both';
  
  // Debug logging after all hooks are declared
  if (process.env.NODE_ENV !== 'production') {
    console.log('AuthProvider: Initial state', { loading, isAuthenticated, userRole, session });
  }

  // Calculate effective role based on actual role and current view context
  const getEffectiveRole = () => {
    // Get role from multiple sources, prioritizing session data
    const actualRole = (session?.user as SessionUser)?.role || userData?.role || userRole || null;
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('AuthProvider: getEffectiveRole - Sources:', {
        sessionRole: (session?.user as SessionUser)?.role,
        userDataRole: userData?.role,
        userRoleState: userRole,
        actualRole,
        currentViewRole,
        currentPath: typeof window !== 'undefined' ? window.location.pathname : ''
      });
    }
    
    if (actualRole === 'both') {
      // For dual-role users, use the current view role or intelligently determine from path
      const hasWindow = typeof window !== 'undefined';
      const currentPath = hasWindow ? window.location.pathname : '';
      let returningFromPayment = false;
      if (hasWindow) {
        try {
          returningFromPayment = sessionStorage.getItem('returnFromPayment') === 'true';
        } catch (err) {
          console.warn('AuthProvider Nav: Unable to read returnFromPayment flag:', err);
        }
      }

      // If on operator path but no view role set, assume operator intent
      if (currentPath.startsWith('/operator') && !currentViewRole) {
        console.log('AuthProvider: On operator path with no view role, assuming operator intent');
        return 'operator';
      }
      
      // If on parent/dashboard path but no view role set, assume parent intent  
      if ((currentPath === '/dashboard' || currentPath.startsWith('/student-dashboard/')) && !currentViewRole) {
        console.log('AuthProvider: On parent path with no view role, assuming parent intent');
        return 'parent';
      }
      
      // Use the current view role or default to parent
      return currentViewRole || 'parent';
    }
    
    return actualRole;
  };

  const manualSyncWithLocalStorage = useCallback(() => {
    const storedUserData = localStorage.getItem('user_data');
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
    }
    const storedChildrenData = localStorage.getItem('children_data');
    if (storedChildrenData) {
      setChildrenData(JSON.parse(storedChildrenData));
    }
  }, []);

  const refreshUserData = useCallback(async (firebaseUser?: User | null) => {
    const userToUse = firebaseUser || user;
    if (!userToUse?.uid) {
      console.log("AuthProvider: refreshUserData - No user.uid, exiting.");
      setUserData(null);
      setUserRole(null);
      setCurrentViewRole(null);
      return;
    }
    console.log(`AuthProvider: Refreshing user data for UID: ${userToUse.uid}`);
    setError(null);

    try {
      // First, check custom claims for role information
      const token = await userToUse.getIdToken(true); // Force refresh
      const decodedToken = await userToUse.getIdTokenResult();
      
      const customRole = decodedToken.claims.role as string;
      const isParentRole = decodedToken.claims.isParent as boolean;
      const isOperatorRole = decodedToken.claims.isOperator as boolean;
      const isProfessionalClaim = decodedToken.claims.isProfessional as boolean;

      console.log('AuthProvider: Token claims:', { customRole, isParentRole, isOperatorRole, isProfessionalClaim });

      if (isProfessionalClaim) {
        setIsProfessional(true);
        localStorage.setItem('solynta_isProfessional', 'true');
      }

      // Determine composite role
      let finalRole = customRole;
      if (isParentRole && isOperatorRole) {
        finalRole = 'both';
        console.log('AuthProvider: User has both parent and operator roles');
      } else if (isParentRole) {
        finalRole = 'parent';
      } else if (isOperatorRole) {
        finalRole = 'operator';
      }
      
      setUserRole(finalRole);
      localStorage.setItem('user_role', finalRole);
      
      // Set default view role if not set
      if (!currentViewRole) {
        // Check localStorage for operator intent first
        const storedViewRole = localStorage.getItem('current_view_role');
        
        if (storedViewRole === 'operator' && (finalRole === 'both' || finalRole === 'operator')) {
          // User explicitly chose operator view
          setCurrentViewRole('operator');
          console.log('AuthProvider: Respecting stored operator view preference');
        } else if (finalRole === 'both') {
          // Default to parent view for users with both roles (if no preference stored)
          setCurrentViewRole('parent');
          console.log('AuthProvider: Setting default view to parent for dual-role user');
        } else if (finalRole === 'parent' || finalRole === 'operator') {
          setCurrentViewRole(finalRole as 'parent' | 'operator');
        }
      }

      // Fetch role-specific data from Firestore
      if (finalRole === 'student') {
        // Fetch student data from Firestore
        const studentId = localStorage.getItem('student_id') || userToUse.uid;
        console.log(`AuthProvider: Fetching student data for ID: ${studentId}`);
        
        const studentRef = doc(db, 'students', studentId);
        const studentSnap = await getDoc(studentRef);
        
        if (studentSnap.exists()) {
          const studentData = { id: studentSnap.id, ...studentSnap.data() };
          console.log('AuthProvider: Student data fetched:', studentData);
          setUserData(studentData);
          localStorage.setItem('user_data', JSON.stringify(studentData));
        } else {
          console.warn(`AuthProvider: No student document found for ID: ${studentId}`);
          setUserData({ id: studentId, role: 'student' });
        }
      } else if (finalRole === 'parent' || finalRole === 'both') {
        // Fetch parent data from Firestore
        console.log(`AuthProvider: Fetching parent data for UID: ${userToUse.uid}`);
        
        const parentRef = doc(db, 'users', userToUse.uid);
        const parentSnap = await getDoc(parentRef);
        
        if (parentSnap.exists()) {
          const parentData = { id: parentSnap.id, ...parentSnap.data() };
          console.log('AuthProvider: Parent data fetched:', parentData);
          setUserData(parentData);
          localStorage.setItem('user_data', JSON.stringify(parentData));
        } else {
          console.warn(`AuthProvider: No parent document found for UID: ${userToUse.uid}`);
          setUserData({ id: userToUse.uid, role: finalRole });
        }
      } else if (finalRole === 'operator') {
        // Fetch operator data from Firestore - check operators collection first, then users
        console.log(`AuthProvider: Fetching operator data for UID: ${userToUse.uid}`);
        
        // First try the operators collection
        const operatorsRef = doc(db, 'operators', userToUse.uid);
        const operatorsSnap = await getDoc(operatorsRef);
        
        if (operatorsSnap.exists()) {
          const operatorData = { id: operatorsSnap.id, ...operatorsSnap.data(), role: 'operator' };
          console.log('AuthProvider: Operator data fetched from operators collection:', operatorData);
          setUserData(operatorData);
          localStorage.setItem('user_data', JSON.stringify(operatorData));
        } else {
          // Fallback to users collection for backwards compatibility
          const operatorRef = doc(db, 'users', userToUse.uid);
          const operatorSnap = await getDoc(operatorRef);
          
          if (operatorSnap.exists()) {
            const operatorData = { id: operatorSnap.id, ...operatorSnap.data() };
            console.log('AuthProvider: Operator data fetched from users collection:', operatorData);
            setUserData(operatorData);
            localStorage.setItem('user_data', JSON.stringify(operatorData));
          } else {
            console.warn(`AuthProvider: No operator document found for UID: ${userToUse.uid}`);
            setUserData({ id: userToUse.uid, role: 'operator' });
          }
        }
      }
    } catch (err) {
      console.error('AuthProvider: Error during refreshUserData:', err);
      setError('Failed to refresh user data. Please try logging in again.');
      setUserData(null);
      setUserRole(null);
      setCurrentViewRole(null);
    }
  }, [user, currentViewRole]);

  const refreshChildrenData = useCallback(async () => {
    if (!user?.uid) return;

    try {
      // Fetch parent dashboard which should include children data
      const dashboardResponse = await fetch(`/api/parent/dashboard?parentId=${user.uid}`);
      const dashboardData = await dashboardResponse.json();

      if (dashboardResponse.ok && dashboardData.success) {
        const children = dashboardData.children || dashboardData.data?.children || [];
        setChildrenData(children);
        localStorage.setItem('children_data', JSON.stringify(children));

        // Keep a simple list of child ids in the user's doc for quick look-ups
        if (children.length > 0) {
          const parentRef = doc(db, 'users', user.uid);
          await updateDoc(parentRef, {
            children: children.map((child: any) => child.id),
          });
        }
        return; // done
      }

      // Fallback: dedicated children endpoint
      const childrenResponse = await fetch(`/api/parent/get-children?parentId=${user.uid}`);
      const childrenResData = await childrenResponse.json();

      if (childrenResponse.ok && childrenResData.success) {
        const children = childrenResData.children || [];
        setChildrenData(children);
        localStorage.setItem('children_data', JSON.stringify(children));

        if (children.length > 0) {
          const parentRef = doc(db, 'users', user.uid);
          await updateDoc(parentRef, {
            children: children.map((child: any) => child.id),
          });
        }
      } else {
        const msg = childrenResData.error || 'Failed to fetch children data';
        throw new Error(msg);
      }
    } catch (err) {
      console.error('AuthProvider: Error fetching children data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch children data');
    }
  }, [user]);

  // Keep Firebase Auth state in sync so Firestore requests are authenticated.
  // Single consolidated onAuthStateChanged listener: token refresh, user-data
  // refresh, localStorage fallback, and sign-out cleanup all happen here.
  useEffect(() => {
    // Ensure this runs client-side
    if (typeof window === 'undefined') return;

    console.log('AuthProvider: Setting up onAuthStateChanged listener.');
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const timestamp = new Date().toISOString();
      console.log(`AuthProvider: onAuthStateChanged fired at ${timestamp}. User object:`,
        firebaseUser ? { uid: firebaseUser.uid, email: firebaseUser.email } : null);

      setUser(firebaseUser);

      if (firebaseUser) {
        console.log(`AuthProvider: onAuthStateChanged - User is present (UID: ${firebaseUser.uid}).`);
        setIsAuthenticated(true);

        // Hydrate from localStorage first for instant UI while Firestore fetch runs.
        // This acts as a fallback if Firestore lookup fails or is still pending.
        const storedUserData = localStorage.getItem('user_data');
        if (storedUserData) {
          try {
            setUserData(JSON.parse(storedUserData));
          } catch (e) {
            console.warn('AuthProvider: Failed to parse stored user_data:', e);
          }
        }
        const storedChildrenData = localStorage.getItem('children_data');
        if (storedChildrenData) {
          try {
            setChildrenData(JSON.parse(storedChildrenData));
          } catch (e) {
            console.warn('AuthProvider: Failed to parse stored children_data:', e);
          }
        }

        try {
          // Force-refresh token ONCE to ensure latest custom claims are available
          await firebaseUser.getIdToken(true);
          const idTokenResult = await firebaseUser.getIdTokenResult();
          console.log('AuthProvider: onAuthStateChanged - Token claims:', idTokenResult.claims);
          if (idTokenResult.claims.isProfessional) {
            setIsProfessional(true);
            localStorage.setItem('solynta_isProfessional', 'true');
          }

          // Refresh user-related data/role ONCE. This authoritative Firestore read
          // will overwrite the localStorage-hydrated state above on success.
          await refreshUserData(firebaseUser);

          // Only refresh children data if user is a parent
          const latestStored = localStorage.getItem('user_data');
          const latestStoredParsed = latestStored ? (() => {
            try { return JSON.parse(latestStored); } catch { return null; }
          })() : null;
          if (latestStoredParsed?.role === 'parent' || latestStoredParsed?.role === 'both') {
            await refreshChildrenData();
          }
        } catch (e) {
          console.warn('AuthProvider: Error refreshing token/user data after Firebase sign-in:', e);
          // On failure, the localStorage-hydrated state remains as fallback.
        }
      } else {
        // Sign-out path: preserve state if NextAuth session is still active/loading
        const latestStatus = nextAuthStatusRef.current;
        const hasNextAuthSession = latestStatus === 'authenticated' && !!nextAuthSessionRef.current?.user;
        if (latestStatus === 'loading' || hasNextAuthSession) {
          console.log('AuthProvider: Firebase user null but NextAuth session still active/loading – preserving state.');
          setIsAuthenticated(true);
        } else {
          console.log('AuthProvider: onAuthStateChanged - User is null and no NextAuth session. Clearing state.');
          setUserData(null);
          setChildrenData(null);
          setUserRole(null);
          setStudentSession(null);
          setIsAuthenticated(false);
          localStorage.removeItem('user_data');
          localStorage.removeItem('children_data');
          localStorage.removeItem('CURRENT_SESSION_KEY');
          localStorage.removeItem('user_role');
          localStorage.removeItem('student_id');
        }
      }

      console.log(`AuthProvider: onAuthStateChanged - Setting loading = false at ${new Date().toISOString()}.`);
      setLoading(false);
    });

    return () => {
      console.log('AuthProvider: Cleaning up onAuthStateChanged listener.');
      unsubscribe();
    };
  }, [refreshUserData, refreshChildrenData]);

  const handleLoginSuccess = useCallback(async (customToken: string, studentIdHint?: string) => {
    // *** REMOVE ALERT ***
    // alert("AuthProvider: handleLoginSuccess CALLED!"); 
    // *** END REMOVE ALERT ***

    console.log("AuthProvider: handleLoginSuccess called."); // Log start
    setLoading(true);
    setError(null);    try {
      // Validate token format
      if (!customToken || typeof customToken !== 'string') {
        throw new Error('Invalid token format: Token is missing or not a string.');
      }
      
      if (!customToken.includes('.')) {
        throw new Error('Invalid token format: Token does not appear to be a valid JWT.');
      }
      
      if (customToken.length < 50) {
        throw new Error('Invalid token format: Token is too short to be valid.');
      }      // Sign in using the custom token with timeout
      console.log("AuthProvider: Calling signInWithCustomToken..."); // Log before call
      
      const signInPromise = signInWithCustomToken(auth, customToken);
      const timeoutPromise = new Promise<UserCredential>((_, reject) =>
        setTimeout(() => reject(new Error('Sign-in timeout: The operation took too long to complete')), 30000)
      );

      const userCredential = await Promise.race([signInPromise, timeoutPromise]);
      const loggedInUser = userCredential.user;
      console.log("AuthProvider: signInWithCustomToken successful. User:", loggedInUser.uid);

      // *** ADD LOGGING: Check auth.currentUser immediately after sign-in ***
      console.log(`AuthProvider: auth.currentUser?.uid immediately after signInWithCustomToken: ${auth.currentUser?.uid}`);

      // Force token refresh to get custom claims
      console.log("AuthProvider: Forcing token refresh (getIdToken(true))..."); // Log before refresh
      await loggedInUser.getIdToken(true);
      console.log("AuthProvider: Token refresh complete."); // Log after refresh

      // *** ADD LOGGING: Check auth.currentUser after token refresh ***
      console.log(`AuthProvider: auth.currentUser?.uid after token refresh: ${auth.currentUser?.uid}`);

      // Use provided studentId or look in localStorage - BUT DO NOT SET AS SESSION ID
      const studentId = studentIdHint || localStorage.getItem('student_id');
                            
      if (studentId) {
        // Only store the student ID, not as a session ID
        localStorage.setItem('student_id', studentId);
        
        // Remove any existing incorrect session ID that might be the student ID
        if (localStorage.getItem('current_session') === studentId) {
          console.log("AuthProvider: Removing incorrect session ID (was set to student ID)");
          localStorage.removeItem('current_session');
        }
        
        // Set claims via API immediately after login
        try {
          const idToken = await loggedInUser.getIdToken();
          const claimResponse = await fetch('/api/auth/set-student-claims', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ studentId: studentId })
          });
          
          if (claimResponse.ok) {
            console.log("AuthProvider: Student claims set successfully");
            // Force another token refresh to get the new claims
            await loggedInUser.getIdToken(true);
          } else {
            let errorMessage = `Status: ${claimResponse.status}`;
            try {
              const errorData = await claimResponse.json();
              errorMessage = errorData.error || errorMessage;
            } catch (jsonError) {
              // If response is not JSON, try to get text
              try {
                const errorText = await claimResponse.text();
                errorMessage = errorText || errorMessage;
              } catch (textError) {
                console.error("AuthProvider: Could not parse error response:", textError);
              }
            }
            console.error("AuthProvider: Failed to set student claims:", errorMessage);
          }
        } catch (e) {
          console.error("AuthProvider: Error setting student claims:", e);
        }
      }

      // Clear any parent-related flags first
      localStorage.removeItem('parent_id');
      localStorage.removeItem('parent_name');
      localStorage.removeItem('parent_role');
      localStorage.removeItem('viewing_as_child');
      localStorage.removeItem('is_parent');

      // Set user role and auth state
      localStorage.setItem('user_role', 'student');
      setUserRole('student');
      setIsAuthenticated(true);
      
      // Clear the progress flag after successful sign-in
      localStorage.removeItem('login_in_progress');
      console.log("AuthProvider: Login successful, cleared login_in_progress flag.");    } catch (err) {
      console.error("AuthProvider: Error signing in with custom token:", err);
      
      let errorMessage = "Failed to sign in with custom token.";
      if (err instanceof Error) {
        if (err.message.includes('auth/quota-exceeded')) {
          errorMessage = "Authentication quota exceeded. Please try again later.";
        } else if (err.message.includes('auth/invalid-custom-token')) {
          errorMessage = "Invalid authentication token. Please try logging in again.";
        } else if (err.message.includes('auth/custom-token-mismatch')) {
          errorMessage = "Authentication token mismatch. Please try logging in again.";
        } else if (err.message.includes('auth/network-request-failed')) {
          errorMessage = "Network error. Please check your internet connection and try again.";
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setUser(null);
      setUserData(null);
      setChildrenData(null);
      setUserRole(null);
      setStudentSession(null);
      // Clear flags/storage on error
      localStorage.removeItem('auth_token');
      localStorage.removeItem('student_id');
      localStorage.removeItem('user_role');
      localStorage.removeItem('current_session');
      localStorage.removeItem('login_in_progress');
      
      // Re-throw the error so it can be caught by the caller
      throw err;
    } finally {
      // setLoading(false); // Loading should be set to false by the onAuthStateChanged listener handling
      console.log("AuthProvider: handleLoginSuccess finally block."); // Log finally
    }
  }, []); // Removed auth dependency as it's globally available

  useRoleDetermination({
    loading,
    user,
    userRole,
    setUserRole,
    setStudentSession,
    setCurrentViewRole,
    status,
    session,
  });

  useAuthNavigation({
    loading,
    status,
    session,
    user,
    userRole,
    currentViewRole,
    router,
    studentDashGraceUntil,
    setStudentDashGraceUntil,
    postPaymentGraceUntil,
    setPostPaymentGraceUntil,
    isProfessional,
  });

  const contextUserRole = (() => {
    const sessionBasedRole = (session?.user as SessionUser | undefined)?.role || null;
    const claimBasedRole = userData?.role || userRole || null;
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

    if (currentPath.startsWith('/operator')) {
      try {
        const storedOperatorSession = typeof window !== 'undefined' ? localStorage.getItem('operator_session') : null;
        if (storedOperatorSession) {
          const parsed = JSON.parse(storedOperatorSession);
          if (parsed?.role === 'operator' || parsed?.role === 'both' || parsed?.role === 'parent') {
            return parsed.role as string;
          }
        }
      } catch {
        // ignore
      }

      if (claimBasedRole) return claimBasedRole;
      if (sessionBasedRole === 'student') return null;
      return sessionBasedRole;
    }

    return sessionBasedRole || claimBasedRole;
  })();

  const contextIsParent = contextUserRole === 'parent' || contextUserRole === 'both';
  const contextIsOperator = contextUserRole === 'operator' || contextUserRole === 'both';

return (
  <AuthContext.Provider
    value={{
      user,
      userData,
      childrenData,
      loading,
      error,
      isAuthenticated: (!!user && !!user.uid) || status === 'authenticated' || (!!session && !!(session as ExtendedSession)?.user?.role),
      // Use SessionUser type for fallbacks
      studentSession: studentSession || ((session?.user as SessionUser)?.role === 'student' ? (session?.user as SessionUser)?.id : null),
      userRole: contextUserRole,
      localEffectiveRole: getEffectiveRole(),
      isParent: contextIsParent,
      isOperator: contextIsOperator,
      currentViewRole,
      setCurrentViewRole: updateCurrentViewRole,
      manualSyncWithLocalStorage,
      refreshUserData,
      refreshChildrenData,
      handleLoginSuccess,        logout: async () => {
          console.log("AuthProvider: Logging out...");
          try {
            await signOut(auth);
            
            // Clear backend performance caches
            try {
              const cacheResponse = await fetch('/api/clear-performance-cache', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
              });
              if (cacheResponse.ok) {
                console.log("AuthProvider: Backend caches cleared successfully");
              } else {
                console.warn("AuthProvider: Failed to clear backend caches, but continuing with logout");
              }
            } catch (cacheError) {
              console.warn("AuthProvider: Error clearing backend caches:", cacheError);
              // Don't fail logout due to cache clearing issues
            }
            
            // Clear local state and storage
            setUser(null);
            setUserData(null);
            setChildrenData(null);
            setUserRole(null);
            setStudentSession(null);
            setCurrentViewRole(null);
            setIsAuthenticated(false);
            
            // Clear all browser storage
            localStorage.clear();
            sessionStorage.clear();
            
            // Clear IndexedDB (if any data is stored there)
            try {
              if ('indexedDB' in window) {
                const databases = await indexedDB.databases();
                databases.forEach(async (db) => {
                  if (db.name) {
                    indexedDB.deleteDatabase(db.name);
                  }
                });
              }
            } catch (idbError) {
              console.warn("AuthProvider: Error clearing IndexedDB:", idbError);
            }
            
            // Clear service worker caches
            try {
              if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(
                  cacheNames.map(cacheName => caches.delete(cacheName))
                );
                console.log("AuthProvider: Service worker caches cleared");
              }
            } catch (swError) {
              console.warn("AuthProvider: Error clearing service worker caches:", swError);
            }
            
            // Clear professional flag
            localStorage.removeItem('solynta_isProfessional');
            setIsProfessional(false);
            console.log("AuthProvider: Logout successful. All caches cleared. Redirecting to login.");
            router.push('/login');
          } catch (err) {
            console.error("AuthProvider: Logout failed:", err);
            setError("Logout failed. Please try again.");
            // Still attempt to clear state/storage even if signOut fails
            setUser(null);
            setUserData(null);
            setChildrenData(null);
            setUserRole(null);
            setStudentSession(null);
            setCurrentViewRole(null);
            setIsAuthenticated(false);
            localStorage.clear();
            sessionStorage.clear();
            router.push('/login'); // Redirect even on error
          }
        },
      }} // Corrected closing braces for value object
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
